import React from 'react';
import { Check, X, DollarSign } from 'lucide-react';

interface Props {
  primaryColor?: string;
}

export default function MemberVsNonMemberTable({ primaryColor = '#0066CC' }: Props) {
  const comparisons = [
    {
      service: 'Diagnostic Fee',
      nonMember: '$99',
      member: '$0 - $69',
      savings: 'Up to $99',
    },
    {
      service: 'Premier Annual Tune-Up',
      nonMember: '$275',
      member: 'INCLUDED',
      savings: '$275',
    },
    {
      service: 'Typical Average Repair',
      nonMember: '$385',
      member: '$308 (20% off) or $0',
      savings: 'Up to $385',
    },
    {
      service: 'Priority Scheduling',
      nonMember: 'Wait 3-5 days',
      member: 'Front of the line',
      savings: 'Time & Comfort',
    },
    {
      service: 'Drain Pan Treatment',
      nonMember: '$129/year',
      member: 'INCLUDED',
      savings: '$129',
    },
  ];

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Member vs Non-Member: The Real Numbers
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Here's what you pay every time you need service — with and without a membership.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-4 px-6 font-bold text-gray-900 text-lg">
                    When You Need...
                  </th>
                  <th className="py-4 px-6 font-bold text-center" style={{ backgroundColor: '#f8f8f8' }}>
                    <div className="flex items-center justify-center gap-2">
                      <X className="w-5 h-5 text-red-500" />
                      <span>Non-Member Pays</span>
                    </div>
                  </th>
                  <th
                    className="py-4 px-6 font-bold text-center text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Member Pays</span>
                    </div>
                  </th>
                  <th className="py-4 px-6 font-bold text-center bg-green-50 text-green-700">
                    <div className="flex items-center justify-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      <span>You Save</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisons.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="py-4 px-6 font-semibold text-gray-900">{row.service}</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block px-3 py-1 rounded-full bg-red-50 text-red-700 font-semibold text-sm">
                        {row.nonMember}
                      </span>
                    </td>
                    <td
                      className="py-4 px-6 text-center font-bold"
                      style={{
                        backgroundColor: `${primaryColor}08`,
                        color: primaryColor,
                      }}
                    >
                      {row.member}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block px-3 py-1 rounded-full bg-green-50 text-green-700 font-semibold text-sm">
                        {row.savings}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Call-out */}
        <div
          className="mt-8 rounded-xl p-6 text-white"
          style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 shrink-0">
              <DollarSign className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold mb-1">
                Average Member Saves $847/Year
              </div>
              <div className="text-white/90">
                Based on typical AC service needs in Florida (1 tune-up + 1 service call + priority access)
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
